import React, { useEffect } from "react";
import { Datepicker } from "@amzn/alchemy-components-react";
import { DEFAULT_LANG } from "src/common/constants";
import { convertDateToLocaleFormat } from "src/common/util";

// See following link for full list of props that are allowed:
// https://alchemy.aft.amazon.dev/components/datepicker
interface DisabledDates {
    to?: Date
}

// Translation keys the Alchemy Datepicker ships (@amzn/alchemy-components 1.2.22, dist/Datepicker). It looks
// up `language` in this table with no fallback, so an unknown key breaks the calendar. Alchemy 1.13.5+ builds
// these names with Intl instead; after upgrading, pass the page locale and remove this table, since keys like
// 'ptBR' aren't valid BCP-47 tags.
const DATEPICKER_LANGUAGES = new Set([
    'af', 'ar', 'bg', 'bs', 'ca', 'cs', 'da', 'de', 'ee', 'el', 'en', 'es', 'fa', 'fi', 'fo', 'fr', 'ge', 'gl',
    'he', 'hr', 'hu', 'id', 'is', 'it', 'ja', 'kk', 'ko', 'lb', 'lt', 'lv', 'mk', 'mn', 'nbNO', 'nl', 'pl', 'ptBR',
    'ro', 'ru', 'sk', 'slSI', 'srCYRL', 'sr', 'sv', 'th', 'tr', 'uk', 'ur', 'vi', 'zh', 'zhHK',
]);

/**
 * Maps a page locale to the Datepicker's language key.
 *
 * Tries the region-qualified key first (pt-BR -> ptBR), then the bare language (lt-LT -> lt), and falls back to
 * English month and day names when the Datepicker has neither (e.g. hi-IN, pt-PT).
 *
 * @param locale the page locale (e.g. en-US).
 * @return a language key the Datepicker supports.
 */
export const getDatepickerLanguage = (locale: string): string => {
    const regional = locale.replace('-', '');   // e.g. ptBR
    const language = locale.split('-')[0];      // e.g. pt

    if (DATEPICKER_LANGUAGES.has(regional)) {
        return regional;
    }

    return DATEPICKER_LANGUAGES.has(language) ? language : 'en';
}

/**
 * Input parameters for creating a LocalizedDatepicker component.
 */
interface LocalizedDatepickerProps {
    id: string,
    label?: string,
    placeholder?: Date,
    selectedDate?: Date,
    onInput?: (...args: any[]) => any,
    status?: string,
    disabledDates?: DisabledDates,
    helpText?: string,
}

/**
 * Component for selecting dates that is localized.
 */
export const LocalizedDatepicker = (datepickerProps: LocalizedDatepickerProps) => {
    const locale = document.documentElement.lang;   // e.g. en-US

    // This gets triggered whenever the locale is changed
    useEffect(() => {
        if (datepickerProps.selectedDate) {
            const el = document.getElementById(datepickerProps.id)!;

            // This is a hacky solution that should be revised once the Alchemy team provides a better way to update
            // format of selectedDate upon changing locale
            // @ts-ignore
            el.selectedDate = new Date(datepickerProps.selectedDate);
        }
    }, [locale]);

    const getLocaleDateFormat = (locale: string) : string => {
        const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());

        return formatObj
            .map(obj => {
                switch (obj.type) {
                    case "day":
                        return "dd";
                    case "month":
                        return "MM";
                    case "year":
                        return "yyyy";
                    default:
                        return obj.value;
                }
            })
            .join("");
    }

    return (
        <Datepicker
            id={datepickerProps.id}
            label={datepickerProps.label}
            placeholder={convertDateToLocaleFormat(locale, datepickerProps.placeholder)}
            onInput={datepickerProps.onInput}
            status={datepickerProps.status}
            language={getDatepickerLanguage(locale)}
            format={getLocaleDateFormat(locale)}
            disabledDates={datepickerProps.disabledDates}
            typeable={locale === DEFAULT_LANG} // There is a bug where date can't be entered via typing when date is not
                                               // in the format of MM/dd/yyyy, so disable typing for now
            helpText={datepickerProps.helpText}
        />
    )
}